import React from 'react';
import { Link } from '@tanstack/react-router';
import { Text, Flex, TagBadge, LoadingOrbit, Markdown, CollapsibleSection } from '@chatsdet/ui';
import { Warning as WarningIcon } from '@phosphor-icons/react';
import {
  useTriage,
  type TriageFinding,
  type TriageFindingKind,
  type TriageFindingSeverity,
  type TriageRunResponse,
  type TriageSubject,
  type TriageVerdict,
} from './useTriage';

const severityColor: Record<
  TriageFindingSeverity,
  'red' | 'orange' | 'yellow' | 'gray'
> = {
  critical: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'gray',
};

const verdictBadge: Record<TriageVerdict, { name: string; color: 'green' | 'yellow' | 'red' }> = {
  all_clear: { name: 'All clear', color: 'green' },
  needs_attention: { name: 'Needs attention', color: 'yellow' },
  urgent: { name: 'Urgent', color: 'red' },
};

/**
 * Exported so a caller rendering a stand-in verdict in the same slot (see
 * `RunTriageLockup`'s fallback) uses this exact chip rather than
 * hand-copying the palette and silently diverging the next time these change.
 */
export const VerdictBadge: React.FC<{ verdict: TriageVerdict }> = ({ verdict }) => (
  <TagBadge
    name={verdictBadge[verdict].name}
    color={verdictBadge[verdict].color}
    hideDot
    size="sm"
    className="self-start"
  />
);

interface FindingSectionConfig {
  kind: TriageFindingKind;
  label: string;
}

const SECTIONS: FindingSectionConfig[] = [
  { kind: 'cascade', label: 'Grouped failures' },
  { kind: 'bug', label: 'Real bugs' },
  { kind: 'maintenance', label: 'Suite maintenance' },
];

interface TriageSectionProps {
  subject: TriageSubject;
  /**
   * Rendered in place of the panel whenever there's nothing triage-specific
   * worth saying — loading, a fetch error, no triage row, or `skipped` (no
   * failures to triage). Required, not optional: this renders into a slot the
   * caller always needs filled, so the panel returning nothing is not a
   * legal outcome. Note the four states are not distinguishable from here —
   * the fallback must hold true while triage is merely still loading, so it
   * cannot assert anything about triage's findings.
   */
  fallback: React.ReactNode;
  /**
   * Keeps polling a `triage: null` response (no row yet) while true — see
   * `useTriage`. The caller (`RunTriageLockup`) computes this from the run
   * being in progress OR having finished recently enough that the triage
   * pipeline could still be running. Without it, a live-watched run whose
   * triage row hasn't appeared yet stops polling once its status stops
   * looking "active", and never picks up the triage that appears (up to ~20
   * minutes later) once the run finishes.
   */
  awaitingTriage: boolean;
}

/**
 * Inline triage panel, rendered into a slot the caller provides (no tab, no
 * BETA label). Renders `fallback` while there's nothing triage-specific worth
 * saying yet — loading, not started, or skipped (no failures to triage) — so
 * the caller can show its own run verdict instead of triage noise. Renders its
 * own content for waiting/running/failed and the completed findings
 * breakdown.
 *
 * A fetch error also renders `fallback`, but is not silently swallowed: see
 * the `isError` branch below.
 */
export const TriageSection: React.FC<TriageSectionProps> = ({
  subject,
  fallback,
  awaitingTriage,
}) => {
  const triageQuery = useTriage(subject, awaitingTriage);

  if (triageQuery.isLoading) return <>{fallback}</>;
  // Falls back to the caller's verdict slot so the column isn't empty, but
  // the failure is still reported: main.tsx's QueryCache({ onError })
  // forwards every query error to Sentry unless shouldSuppressQuerySentry()
  // filters it, and the generic Error thrown by useTriage's queryFn isn't
  // one of the filtered cases (see apps/web/src/lib/sentry-error-filter.ts).
  // This is not a silent swallow — the UI just shows the fallback; telemetry
  // is still loud.
  if (triageQuery.isError) return <>{fallback}</>;

  const triage = triageQuery.data?.triage ?? null;

  if (!triage) return <>{fallback}</>;
  if (triage.status === 'skipped') return <>{fallback}</>;

  if (triage.status === 'waiting') {
    const ready = Math.max(0, triage.diagnosticsTotal - triage.diagnosticsPending);
    return (
      <Flex direction="column" gap="md" className="py-6 items-center">
        <LoadingOrbit size="md" />
        <Text size="sm" className="text-muted-foreground">
          Waiting on diagnostics ({ready} / {triage.diagnosticsTotal} ready)
        </Text>
      </Flex>
    );
  }

  if (triage.status === 'running') {
    return (
      <Flex direction="column" gap="md" className="py-6 items-center">
        <LoadingOrbit size="md" />
        <Text size="sm" className="text-muted-foreground">AI is triaging failures…</Text>
      </Flex>
    );
  }

  if (triage.status === 'failed') {
    return (
      <Flex direction="column" gap="xs">
        <Flex align="center" gap="sm">
          <WarningIcon size={20} className="text-destructive" />
          <Text size="sm" weight="medium">Triage failed</Text>
        </Flex>
        <Text size="sm" className="text-muted-foreground">
          {triage.errorMessage ?? 'The triage agent did not finish.'}
        </Text>
      </Flex>
    );
  }

  return <TriageCompleted triage={triage} subject={subject} />;
};

const TriageCompleted: React.FC<{ triage: TriageRunResponse; subject: TriageSubject }> = ({
  triage,
  subject,
}) => {
  return (
    <Flex direction="column" gap="lg">
      <Flex direction="column" gap="xs">
        {triage.verdict && <VerdictBadge verdict={triage.verdict} />}
        {triage.headline && <Text size="md">{triage.headline}</Text>}

        {triage.summaryMd && (
          <Markdown className="text-sm text-muted-foreground">
            {triage.summaryMd}
          </Markdown>
        )}

        <Flex align="center" gap="sm" wrap>
          <Text size="xs" className="text-muted-foreground">
            {triage.failureCount === 1 ? '1 failure' : `${triage.failureCount} failures`} across{' '}
            {triage.findings.length === 1 ? '1 finding' : `${triage.findings.length} findings`}
          </Text>
          {triage.newFailureCount > 0 && (
            <>
              <Text size="xs" className="text-muted-foreground">·</Text>
              <Text size="xs" className="text-muted-foreground">
                {triage.newFailureCount} newly failing
              </Text>
            </>
          )}
          {triage.diagnosticsTimedOut && (
            <TagBadge name="Partial diagnostics" color="yellow" hideDot size="sm" />
          )}
        </Flex>
      </Flex>

      {SECTIONS.map(({ kind, label }) => {
        const findings = triage.findings
          .filter((f) => f.kind === kind)
          .sort((a, b) => a.rank - b.rank);
        if (findings.length === 0) return null;

        // Collapsed by default to keep the panel quiet — a section only opens
        // on its own if it holds a critical finding, so the reader's
        // attention goes straight to the one thing that warrants it.
        //
        // `CollapsibleSection` seeds its state from `defaultExpanded` once at
        // mount and never resyncs, so this is only correct because
        // `TriageCompleted` mounts with final findings: `useTriage`
        // stops polling as soon as status leaves pending/waiting/running, and
        // this branch is reached only for `completed`. If findings ever start
        // being revised in place on a completed run, a section that becomes
        // critical later would stay collapsed — switch to the controlled
        // `isExpanded` prop at that point.
        const defaultExpanded = findings.some((f) => f.severity === 'critical');

        return (
          <CollapsibleSection
            key={kind}
            label={label}
            count={findings.length}
            defaultExpanded={defaultExpanded}
            hideBorder
            triggerClassName="mx-0"
          >
            <div>
              {findings.map((finding) => (
                <FindingCard key={finding.id} finding={finding} subject={subject} />
              ))}
            </div>
          </CollapsibleSection>
        );
      })}
    </Flex>
  );
};

const FindingCard: React.FC<{ finding: TriageFinding; subject: TriageSubject }> = ({
  finding,
  subject,
}) => {
  const runsWithLinks = finding.runs.filter(
    (r): r is typeof r & { testRunId: string } => r.testRunId !== null
  );

  // A finding can carry two runs of the same workflow (e.g. an initial run
  // and its retry) — disambiguate those by appending the run's short id so
  // the link row doesn't render the same workflow name twice with no way to
  // tell them apart.
  const nameCounts = new Map<string, number>();
  for (const r of runsWithLinks) {
    const name = r.workflowName ?? r.workflowRunId.slice(0, 8);
    nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
  }

  return (
    <div className="py-3 first:pt-0 last:pb-0 border-b border-border/50 last:border-b-0">
      <Flex align="center" gap="xs" wrap className="mb-2">
        <Text size="sm" weight="medium">{finding.title}</Text>
        <TagBadge
          name={finding.newFailureCount > 0 ? `${finding.severity} · new` : finding.severity}
          color={severityColor[finding.severity]}
          hideDot
          size="sm"
        />
      </Flex>

      {/* Explicit size, not `prose-sm`: @tailwindcss/typography is not installed,
          so `prose*` classes emit nothing and the body would render at the 16px
          base — larger than its own `sm` title. */}
      <Markdown className="text-sm">{finding.bodyMd}</Markdown>

      {(runsWithLinks.length > 0 || finding.issues.length > 0) && (
        <Flex align="center" gap="sm" wrap className="mt-3 pt-3 border-t border-border/60">
          {runsWithLinks.map((r) => {
            const name = r.workflowName ?? r.workflowRunId.slice(0, 8);
            const isDuplicateName = (nameCounts.get(name) ?? 0) > 1;
            const label = isDuplicateName ? `${name} (${r.workflowRunId.slice(0, 8)})` : name;
            const linkClassName =
              'text-xs font-medium text-primary underline underline-offset-4 hover:text-primary/80 transition-colors';
            // `testRunId` means a native test-run id for an execution subject, and
            // an external run item id for an externalRun subject — same field,
            // different route per kind.
            return subject.kind === 'execution' ? (
              <Link
                key={r.workflowRunId}
                to="/runs/tests/$testRunId"
                params={{ testRunId: r.testRunId }}
                className={linkClassName}
              >
                {label}
                {r.isNew ? ' (new)' : ''}
              </Link>
            ) : (
              <Link
                key={r.workflowRunId}
                to="/runs/external/$runId/item/$itemId"
                params={{ runId: subject.id, itemId: r.testRunId }}
                className={linkClassName}
              >
                {label}
                {r.isNew ? ' (new)' : ''}
              </Link>
            );
          })}
          {finding.issues.map((issue) => (
            <Link
              key={issue.issueId}
              to="/issues"
              search={{ issueId: issue.issueId }}
              className="text-xs font-medium text-primary underline underline-offset-4 hover:text-primary/80 transition-colors"
            >
              {issue.shortcode}
            </Link>
          ))}
        </Flex>
      )}
    </div>
  );
};
